/**
 * Each file in this folder does the following:
 *
 * Map a CMS component to a builder, which provides the component
 * and a function to build the props for the component. This map
 * is intentionally restrictive, as you should not be building
 * any CMS-specific components. This is intended to map between
 * the CMS data & our Storybook components, with a thin data
 * translation in between. If additional components are needed,
 * build them in Storybook then add them here. Do *not* build
 * custom components just for the CMS.
 *
 * This file re-exports the builders, allowing us to gather them
 * into a single, easily-referable object.
 *
 * This is also the only folder in the whole project allowed to
 * use `snake_case` file names, as that maps back to the component
 * names in the CMS.
 */

import * as comparison_chart from './comparison_chart';
import * as faq_list from './faq_list';
import * as footer from './footer';
import * as header_reference from './header_reference';
import * as header from './header';
import * as hero from './hero';
import * as press_and_publications from './press_and_publications';
import * as reason_reference from './reason_reference';
import * as reason from './reason';
import * as hover_image_card_reference from './hover_image_card_reference';
import * as hover_image_card from './hover_image_card';
import * as hover_image_cards from './hover_image_cards';
import * as illustration_pill from './illustration_pill';
import * as illustration_pills from './illustration_pills';
import * as split_screen_grid from './split_screen_grid';
import * as testimonials from './testimonials';
import * as testimonial from './testimonial';
import * as quotes from './quotes';
import * as quote from './quote';
import * as timeline_scroll_promo from './timeline_scroll_promo';
import * as value_props from './value_props';
import * as flexible_picture from './flexible_picture';
import * as heading from './heading';
import * as testimonial_highlight from './testimonial_highlight';
import * as blog_links from './blog_links';
import * as rich_text from './rich_text';
import * as split_screen_promo from './split_screen_promo';
import * as split_screen_list_body from './split_screen_list_body';
import * as recipe_overview from './recipe_overview';
import * as list from './list';
import * as testimonial_round_image from './testimonial_round_image';
import * as cta_button from './cta_button';
import * as image_carousel_cell from './image_carousel_cell';
import * as grid from './grid';
import * as cell from './cell';
import * as trust_pilot from './trust_pilot';
import * as testimonial_square_image from './testimonial_square_image';
import * as food_comparison from './food_comparison';
import * as press_quote from './press_quote';
import * as faq_question from './faq_question';
import * as split_screen_grid_item from './split_screen_grid_item';
import * as recipe_nutrient from './recipe_nutrient';
import * as full_ingredients_modal from './full_ingredients_modal';
import * as comparison_chart_row from './comparison_chart_row';
import * as breadcrumbs from './breadcrumbs';
import * as box_contents from './box_contents';
import * as customer_testimonials from './customer_testimonials';
import * as image from './image';
import * as simplified_hero from './simplified_hero';
import * as values_banner from './values_banner';
import * as plans_carousel from './plans_carousel';
import * as endorsements from './endorsements';
import * as full_screen_callout from './full_screen_callout';
import * as simple_callout from './simple_callout';
import * as split_panels from './split_panels';
import * as split_panel_body from './split_panel_body';
import * as offer_callout from './offer_callout';
import * as recipes_details from './recipes_details';
import * as page_tabs from './page_tabs';
import * as gladly from './gladly';
import * as products_summary_card from './products_summary_card';
import * as add_on_summary_card from './add_on_summary_card';
import * as add_on_summary_cards from './add_on_summary_cards';
import * as slideout_menu_link from './slideout_menu_link';
import * as slide_down_menu_link from './slide_down_menu_link';
import * as recipes_combiner from './recipes_combiner';
import * as nutrients_list_description from './nutrients_list_description';
import * as recipe_information_section from './recipe_information_section';
import * as full_ingredients from './full_ingredients';
import * as addon_sections from './addon_sections';
import * as extra_sections from './extra_sections';
import * as addon_card from './addon_card';
import * as extra_card from './extra_card';
import * as account_dashboard from './account_dashboard';
import * as countdown_clock_heading from './countdown_clock_heading';
import * as learn_more from './learn_more';
import * as feeding_guidelines from './feeding_guidelines';
import * as feeding_guidelines_dropdown from './feeding_guidelines_dropdown';
import * as feeding_guidelines_recipes from './feeding_guidelines_recipes';
import * as before_after_testimonial from './before_after_testimonial';
import * as facts_table from './facts_table';
import * as timeline from './timeline';
import * as product_values from './product_values';
import * as email_capture from './email_capture';
import * as email_capture_bar from './email_capture_bar';
import * as information_stats from './information_stats';
import * as info_stat_block from './info_stat_block';
import * as product_values_group from './product_values_group';
import * as simple_callout_panel from './simple_callout_panel';
import * as vertical_panel from './vertical_panel';
import * as vertical_panel_body from './vertical_panel_body';
import * as vertical_panel_with_image_rails from './vertical_panel_with_image_rails';
import * as store_locator_map from './store_locator_map';
import * as events_page from './events_page';
import * as app_cta_component from './app_cta_component';
import * as flagship_ab_test from './flagship_ab_test';
import * as mux_video from './mux_video';
import * as simple_callout_promo from './simple_callout_promo';
import * as video_hero from './video_hero';
import * as get_social_component from './get_social_component';
import * as confirmation_poll from './confirmation_poll';
import * as social_button from './social_button';
import * as confirmation_banner from './confirmation_banner';
import * as simple_callout_card from './simple_callout_card';
import * as simple_callout_cards from './simple_callout_cards';
import * as rapid_cro_video_hero_content_test from './rapid_cro_video_hero_content_test';
import * as swipe_hero from './swipe_hero';
import * as tabs from './tabs';
import * as details_tab from './details_tab';
import * as ingredients_tab from './ingredients_tab';
import * as feeding_tab from './feeding_tab';
import * as trust_pilot_list from './trust_pilot_list';
import * as try_my_ollie from './try_my_ollie';
import * as discount_banner from './discount_banner';
import * as cookie_banner from './cookie_banner';

export {
  comparison_chart,
  faq_list,
  footer,
  header_reference,
  header,
  hero,
  press_and_publications,
  reason_reference,
  reason,
  hover_image_card_reference,
  hover_image_card,
  hover_image_cards,
  illustration_pill,
  illustration_pills,
  split_screen_grid,
  testimonials,
  testimonial,
  quotes,
  quote,
  timeline_scroll_promo,
  value_props,
  flexible_picture,
  heading,
  image,
  testimonial_highlight,
  blog_links,
  simplified_hero,
  rich_text,
  split_screen_promo,
  split_screen_list_body,
  recipe_overview,
  list,
  testimonial_round_image,
  cta_button,
  image_carousel_cell,
  grid,
  cell,
  trust_pilot,
  testimonial_square_image,
  food_comparison,
  press_quote,
  faq_question,
  split_screen_grid_item,
  recipe_nutrient,
  full_ingredients_modal,
  comparison_chart_row,
  breadcrumbs,
  box_contents,
  values_banner,
  plans_carousel,
  endorsements,
  full_screen_callout,
  simple_callout,
  split_panels,
  split_panel_body,
  offer_callout,
  recipes_details,
  page_tabs,
  customer_testimonials,
  gladly,
  slideout_menu_link,
  slide_down_menu_link,
  products_summary_card,
  add_on_summary_card,
  add_on_summary_cards,
  recipes_combiner,
  nutrients_list_description,
  recipe_information_section,
  full_ingredients,
  addon_sections,
  extra_sections,
  addon_card,
  extra_card,
  account_dashboard,
  countdown_clock_heading,
  learn_more,
  feeding_guidelines,
  feeding_guidelines_dropdown,
  feeding_guidelines_recipes,
  before_after_testimonial,
  facts_table,
  timeline,
  product_values,
  email_capture,
  email_capture_bar,
  information_stats,
  info_stat_block,
  product_values_group,
  simple_callout_panel,
  vertical_panel,
  vertical_panel_body,
  vertical_panel_with_image_rails,
  store_locator_map,
  events_page,
  app_cta_component,
  flagship_ab_test,
  simple_callout_promo,
  video_hero,
  mux_video,
  get_social_component,
  confirmation_poll,
  social_button,
  confirmation_banner,
  simple_callout_card,
  simple_callout_cards,
  rapid_cro_video_hero_content_test,
  swipe_hero,
  tabs,
  details_tab,
  ingredients_tab,
  feeding_tab,
  trust_pilot_list,
  try_my_ollie,
  discount_banner,
  cookie_banner,
};
